import {
  buffer,
  circle,
  convex,
  featureCollection,
  lineString,
  point,
  union,
} from '@turf/turf';
import type {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
  Position,
} from 'geojson';
import type { DashboardMapItem } from '@/core/interfaces/Dashboard';
import { MAP_WAREHOUSE_HIGHLIGHT } from '@/core/constants/dashboard-map.constants';
import {
  isMainlandCoordinate,
  normalizeLngLat,
  VIETNAM_MAINLAND_CLIP,
} from '@/core/helpers/dashboard-map';

function fallbackNationalBounds(): [[number, number], [number, number]] {
  return [
    [VIETNAM_MAINLAND_CLIP.minLng, VIETNAM_MAINLAND_CLIP.minLat],
    [VIETNAM_MAINLAND_CLIP.maxLng, VIETNAM_MAINLAND_CLIP.maxLat],
  ];
}

function isFinitePosition(coord: Position): coord is [number, number] {
  return (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(coord[0]) &&
    Number.isFinite(coord[1])
  );
}

function normalizePosition(coord: Position): [number, number] | null {
  if (!isFinitePosition(coord)) {
    return null;
  }
  const normalized = normalizeLngLat(coord[0], coord[1]);
  if (!normalized) {
    return null;
  }
  return [normalized.longitude, normalized.latitude];
}

function ringCentroid(ring: Position[]): [number, number] | null {
  const points = ring
    .map((coord) => normalizePosition(coord))
    .filter((coord): coord is [number, number] => coord != null);
  if (!points.length) {
    return null;
  }
  const sum = points.reduce(
    (acc, [lng, lat]) => {
      acc[0] += lng;
      acc[1] += lat;
      return acc;
    },
    [0, 0]
  );
  return [sum[0] / points.length, sum[1] / points.length];
}

function sanitizeRing(ring: Position[]): [number, number][] | null {
  const cleaned: [number, number][] = [];
  for (const coord of ring) {
    const normalized = normalizePosition(coord);
    if (!normalized) {
      continue;
    }
    const prev = cleaned[cleaned.length - 1];
    if (prev && prev[0] === normalized[0] && prev[1] === normalized[1]) {
      continue;
    }
    cleaned.push(normalized);
  }

  if (cleaned.length < 4) {
    return null;
  }

  const first = cleaned[0];
  const last = cleaned[cleaned.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    cleaned.push([first[0], first[1]]);
  }

  if (cleaned.length < 4) {
    return null;
  }

  return cleaned;
}

function sanitizePolygonCoordinates(
  coordinates: Position[][]
): Position[][] | null {
  const rings: Position[][] = [];
  for (const ring of coordinates) {
    const sanitized = sanitizeRing(ring);
    if (!sanitized) {
      continue;
    }
    rings.push(sanitized);
  }
  return rings.length ? rings : null;
}

/**
 * Keep only mainland polygon parts — drops Hoàng Sa / Trường Sa outliers that
 * blow up province fitBounds and selected-province visuals.
 */
function filterMainlandPolygonParts(
  geometry: Polygon | MultiPolygon
): Polygon | MultiPolygon | null {
  if (geometry.type === 'Polygon') {
    const rings = sanitizePolygonCoordinates(geometry.coordinates);
    if (!rings) {
      return null;
    }
    const centroid = ringCentroid(rings[0]);
    if (!centroid || !isMainlandCoordinate(centroid[0], centroid[1])) {
      return null;
    }
    return { type: 'Polygon', coordinates: rings };
  }

  const kept: Position[][][] = [];
  for (const polygon of geometry.coordinates) {
    const rings = sanitizePolygonCoordinates(polygon);
    if (!rings) {
      continue;
    }
    const centroid = ringCentroid(rings[0]);
    if (!centroid || !isMainlandCoordinate(centroid[0], centroid[1])) {
      continue;
    }
    kept.push(rings);
  }

  if (!kept.length) {
    return null;
  }
  if (kept.length === 1) {
    return { type: 'Polygon', coordinates: kept[0] };
  }
  return { type: 'MultiPolygon', coordinates: kept };
}

function visitPositions(
  geometry: Polygon | MultiPolygon,
  visit: (coord: [number, number]) => void
): void {
  if (geometry.type === 'Polygon') {
    geometry.coordinates[0]?.forEach((coord) => {
      const normalized = normalizePosition(coord);
      if (normalized) {
        visit(normalized);
      }
    });
    return;
  }

  geometry.coordinates.forEach((polygon) => {
    polygon[0]?.forEach((coord) => {
      const normalized = normalizePosition(coord);
      if (normalized) {
        visit(normalized);
      }
    });
  });
}

export function getBoundsFromGeometry(
  geometry: Polygon | MultiPolygon
): [[number, number], [number, number]] | null {
  let minLng = Number.POSITIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;

  visitPositions(geometry, ([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  if (!Number.isFinite(minLng) || !Number.isFinite(maxLng)) {
    return null;
  }

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/**
 * Dim mask for geography outside Vietnam (world/SEA rectangle minus VN holes).
 * Softens foreign regions without a full globe polygon difference.
 */
export function buildOutsideVietnamMask(
  vietnamBoundary: FeatureCollection
): Feature<Polygon> {
  const vietnam = vietnamBoundary.features[0];
  const outerRing: [number, number][] = [
    [93.5, 3.5],
    [117.5, 3.5],
    [117.5, 29.5],
    [93.5, 29.5],
    [93.5, 3.5],
  ];

  const holes: [number, number][][] = [];
  if (vietnam?.geometry.type === 'Polygon') {
    const ring = sanitizeRing(vietnam.geometry.coordinates[0] ?? []);
    if (ring) {
      holes.push([...ring].reverse());
    }
  } else if (vietnam?.geometry.type === 'MultiPolygon') {
    for (const polygon of vietnam.geometry.coordinates) {
      const ring = sanitizeRing(polygon[0] ?? []);
      if (ring) {
        holes.push([...ring].reverse());
      }
    }
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [outerRing, ...holes],
    },
  };
}

/** @deprecated use buildOutsideVietnamMask */
export function buildSoutheastAsiaDimMask(
  vietnamBoundary: FeatureCollection
): Feature<Polygon> {
  return buildOutsideVietnamMask(vietnamBoundary);
}

/**
 * Build the national Vietnam outline from the same province admin polygons
 * used for province boundaries. No Bézier / curve smoothing — exact ring edges.
 */
export function buildNationalBoundaryFromProvinces(
  provinces: FeatureCollection
): FeatureCollection {
  const polygons: Feature<Polygon | MultiPolygon>[] = [];

  for (const feature of provinces.features) {
    if (
      feature.geometry?.type !== 'Polygon' &&
      feature.geometry?.type !== 'MultiPolygon'
    ) {
      continue;
    }
    polygons.push(feature as Feature<Polygon | MultiPolygon>);
  }

  if (!polygons.length) {
    return { type: 'FeatureCollection', features: [] };
  }

  // Sequential union keeps original admin vertices (no curve smoothing).
  let combined: Feature<Polygon | MultiPolygon> | null = null;
  for (const feature of polygons) {
    if (!combined) {
      combined = {
        type: 'Feature',
        properties: { name: 'Vietnam' },
        geometry: feature.geometry,
      };
      continue;
    }

    try {
      const merged = union(combined, feature);
      if (merged?.geometry) {
        combined = {
          type: 'Feature',
          properties: { name: 'Vietnam' },
          geometry: merged.geometry as Polygon | MultiPolygon,
        };
      }
    } catch {
      // Keep previous combined geometry on union failure.
    }
  }

  return {
    type: 'FeatureCollection',
    features: combined ? [combined] : [],
  };
}

/**
 * Province centroid labels for the custom place-label symbol layer.
 */
export function buildProvincePlaceLabels(
  provinces: FeatureCollection
): FeatureCollection {
  const features: Feature[] = [];

  for (const feature of provinces.features) {
    const geometry = feature.geometry;
    if (
      !geometry ||
      (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
    ) {
      continue;
    }

    const name = String(
      feature.properties?.province_name ??
        feature.properties?.shapeName ??
        feature.properties?.name ??
        ''
    ).trim();
    if (!name) {
      continue;
    }

    let centroid: [number, number] | null = null;
    if (geometry.type === 'Polygon') {
      centroid = ringCentroid(geometry.coordinates[0] ?? []);
    } else {
      let bestRing: Position[] | null = null;
      for (const polygon of geometry.coordinates) {
        const ring = polygon[0];
        if (!ring) {
          continue;
        }
        if (!bestRing || ring.length > bestRing.length) {
          bestRing = ring;
        }
      }
      centroid = bestRing ? ringCentroid(bestRing) : null;
    }

    if (!centroid) {
      continue;
    }

    features.push({
      type: 'Feature',
      properties: {
        name,
        label_level: 'province',
        rank: 1,
        province_code: feature.properties?.province_code ?? '',
      },
      geometry: {
        type: 'Point',
        coordinates: centroid,
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Normalize district polygons and keep properties used by map filters/labels.
 */
export function normalizeDistrictGeoJson(
  collection: FeatureCollection
): FeatureCollection {
  const features: Feature[] = [];

  for (const feature of collection.features) {
    const geometry = feature.geometry;
    if (
      !geometry ||
      (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
    ) {
      continue;
    }

    const props = feature.properties ?? {};
    const districtName = String(
      props.district_name ?? props.shapeName ?? props.name ?? ''
    ).trim();
    if (!districtName) {
      continue;
    }

    features.push({
      ...feature,
      properties: {
        ...props,
        district_name: districtName,
        name: districtName,
        province_code: String(props.province_code ?? ''),
        label_level: 'district',
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * District centroid labels for the district symbol layer.
 */
export function buildDistrictPlaceLabels(
  districts: FeatureCollection
): FeatureCollection {
  const features: Feature[] = [];

  for (const feature of districts.features) {
    const geometry = feature.geometry;
    if (
      !geometry ||
      (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
    ) {
      continue;
    }

    const name = String(
      feature.properties?.district_name ??
        feature.properties?.name ??
        ''
    ).trim();
    if (!name) {
      continue;
    }

    let centroid: [number, number] | null = null;
    if (geometry.type === 'Polygon') {
      centroid = ringCentroid(geometry.coordinates[0] ?? []);
    } else {
      let bestRing: Position[] | null = null;
      for (const polygon of geometry.coordinates) {
        const ring = polygon[0];
        if (!ring) {
          continue;
        }
        if (!bestRing || ring.length > bestRing.length) {
          bestRing = ring;
        }
      }
      centroid = bestRing ? ringCentroid(bestRing) : null;
    }

    if (!centroid) {
      continue;
    }

    features.push({
      type: 'Feature',
      properties: {
        name,
        label_level: 'district',
        rank: 2,
        province_code: feature.properties?.province_code ?? '',
      },
      geometry: {
        type: 'Point',
        coordinates: centroid,
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

export function getVietnamBoundsFromGeoJson(
  vietnamBoundary: FeatureCollection
): [[number, number], [number, number]] {
  const vietnam = vietnamBoundary.features[0];
  if (
    !vietnam ||
    (vietnam.geometry.type !== 'Polygon' &&
      vietnam.geometry.type !== 'MultiPolygon')
  ) {
    return fallbackNationalBounds();
  }

  const filtered = filterMainlandPolygonParts(vietnam.geometry);
  const bounds = filtered ? getBoundsFromGeometry(filtered) : null;
  return bounds ?? fallbackNationalBounds();
}

export function normalizeProvinceGeoJson(
  collection: FeatureCollection
): FeatureCollection {
  const features: Feature[] = [];

  for (const feature of collection.features) {
    const geometry = feature.geometry;
    if (
      !geometry ||
      (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
    ) {
      continue;
    }

    const filtered = filterMainlandPolygonParts(geometry);
    if (!filtered) {
      continue;
    }

    const props = feature.properties ?? {};
    const iso = String(props.shapeISO ?? props.province_code ?? '');
    const provinceCode = iso.includes('-') ? iso.split('-').pop() ?? iso : iso;
    const provinceName = String(
      props.province_name ?? props.shapeName ?? props.name ?? ''
    );

    features.push({
      ...feature,
      geometry: filtered,
      properties: {
        ...props,
        province_code: provinceCode,
        province_name: provinceName,
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

export function getProvinceBoundsByCode(
  provinces: FeatureCollection | null,
  provinceCode: string | null | undefined
): [[number, number], [number, number]] | null {
  if (!provinces || !provinceCode) {
    return null;
  }

  const code = String(provinceCode);
  const feature = provinces.features.find((item) => {
    const props = item.properties ?? {};
    return (
      String(props.province_code ?? '') === code ||
      String(props.shapeISO ?? '').endsWith(`-${code}`)
    );
  });

  if (
    !feature ||
    (feature.geometry.type !== 'Polygon' &&
      feature.geometry.type !== 'MultiPolygon')
  ) {
    return null;
  }

  return getBoundsFromGeometry(feature.geometry);
}

function uniqueValidCoordinates(
  markers: DashboardMapItem[]
): [number, number][] {
  const seen = new Set<string>();
  const coordinates: [number, number][] = [];

  for (const marker of markers) {
    const normalized = normalizeLngLat(marker.longitude, marker.latitude);
    if (!normalized) {
      continue;
    }
    const key = `${normalized.longitude.toFixed(5)},${normalized.latitude.toFixed(5)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    coordinates.push([normalized.longitude, normalized.latitude]);
  }

  return coordinates;
}

function isValidHighlightPolygon(
  feature: Feature<Polygon> | null
): feature is Feature<Polygon> {
  if (!feature || feature.geometry.type !== 'Polygon') {
    return false;
  }
  const ring = feature.geometry.coordinates[0];
  if (!ring || ring.length < 4) {
    return false;
  }

  const bounds = getBoundsFromGeometry(feature.geometry);
  if (!bounds) {
    return false;
  }

  const [[west, south], [east, north]] = bounds;
  const width = east - west;
  const height = north - south;

  // Reject degenerate / globe-spanning artifacts from bad buffers.
  if (width > 3 || height > 3 || width < 0 || height < 0) {
    return false;
  }
  if (
    west < VIETNAM_MAINLAND_CLIP.minLng - 1 ||
    east > VIETNAM_MAINLAND_CLIP.maxLng + 1 ||
    south < VIETNAM_MAINLAND_CLIP.minLat - 1 ||
    north > VIETNAM_MAINLAND_CLIP.maxLat + 1
  ) {
    return false;
  }

  return true;
}

/**
 * Build a highlighted warehouse region:
 * - 1 unique point  -> circle
 * - 2 unique points -> buffered corridor (if far enough apart)
 * - 3+ unique points -> convex hull + buffer
 */
export function buildWarehouseAreaHighlight(
  markers: DashboardMapItem[]
): Feature<Polygon> | null {
  const coordinates = uniqueValidCoordinates(markers);
  if (!coordinates.length) {
    return null;
  }

  if (coordinates.length === 1) {
    const feature = circle(
      coordinates[0],
      MAP_WAREHOUSE_HIGHLIGHT.singleRadiusKm,
      {
        units: 'kilometers',
        steps: 64,
      }
    ) as Feature<Polygon>;
    return isValidHighlightPolygon(feature) ? feature : null;
  }

  if (coordinates.length === 2) {
    const [a, b] = coordinates;
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    const degDistance = Math.sqrt(dx * dx + dy * dy);
    if (degDistance < 0.0008) {
      const feature = circle(a, MAP_WAREHOUSE_HIGHLIGHT.singleRadiusKm, {
        units: 'kilometers',
        steps: 64,
      }) as Feature<Polygon>;
      return isValidHighlightPolygon(feature) ? feature : null;
    }

    const corridor = lineString(coordinates);
    const feature = buffer(corridor, MAP_WAREHOUSE_HIGHLIGHT.bufferKm, {
      units: 'kilometers',
      steps: 16,
    }) as Feature<Polygon> | null;
    return isValidHighlightPolygon(feature) ? feature : null;
  }

  const points = featureCollection(coordinates.map((coord) => point(coord)));
  const hull = convex(points);
  if (!hull) {
    const feature = circle(coordinates[0], MAP_WAREHOUSE_HIGHLIGHT.singleRadiusKm, {
      units: 'kilometers',
      steps: 64,
    }) as Feature<Polygon>;
    return isValidHighlightPolygon(feature) ? feature : null;
  }

  const feature = buffer(hull, MAP_WAREHOUSE_HIGHLIGHT.bufferKm, {
    units: 'kilometers',
    steps: 16,
  }) as Feature<Polygon> | null;

  return isValidHighlightPolygon(feature) ? feature : null;
}
